<script setup>
const INSTALL_DISMISSED_KEY = "alomonshi-pwa-install-dismissed"
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000

const installEvent = shallowRef(null)
const registration = shallowRef(null)
const showInstall = ref(false)
const showUpdate = ref(false)
const isIos = ref(false)
let isReloading = false

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true

const wasRecentlyDismissed = () => {
  const dismissedAt = Number(localStorage.getItem(INSTALL_DISMISSED_KEY))

  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION
}

const revealInstallPrompt = () => {
  if (!isStandalone() && !wasRecentlyDismissed()) showInstall.value = true
}

const dismiss = () => {
  showInstall.value = false
  showUpdate.value = false
  localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()))
}

const install = async () => {
  if (!installEvent.value) return

  await installEvent.value.prompt()
  const { outcome } = await installEvent.value.userChoice

  installEvent.value = null
  showInstall.value = false

  if (outcome === "dismissed")
    localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()))
}

const applyUpdate = () => {
  registration.value?.waiting?.postMessage({ type: "SKIP_WAITING" })
}

const watchForUpdate = currentRegistration => {
  registration.value = currentRegistration

  if (currentRegistration.waiting && navigator.serviceWorker.controller)
    showUpdate.value = true

  currentRegistration.addEventListener("updatefound", () => {
    const worker = currentRegistration.installing

    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller)
        showUpdate.value = true
    })
  })
}

onMounted(async () => {
  // Native app updates are delivered by APK, not the website service worker.
  if (window.Capacitor?.isNativePlatform?.()) return

  isIos.value = /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)

  window.addEventListener("beforeinstallprompt", event => {
    event.preventDefault()
    installEvent.value = event
    revealInstallPrompt()
  })

  window.addEventListener("appinstalled", () => {
    showInstall.value = false
    localStorage.removeItem(INSTALL_DISMISSED_KEY)
  })

  navigator.serviceWorker?.addEventListener("controllerchange", () => {
    if (isReloading) return

    isReloading = true
    window.location.reload()
  })

  if ("serviceWorker" in navigator) {
    try {
      const currentRegistration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none",
      })

      watchForUpdate(currentRegistration)
      await currentRegistration.update()
    } catch (error) {
      console.warn("PWA service worker registration failed", error)
    }
  }

  if (isIos.value) revealInstallPrompt()
})
</script>

<template>
  <Transition name="pwa-prompt">
    <aside
      v-if="showUpdate || showInstall"
      class="pwa-prompt"
      dir="rtl"
      role="status"
      aria-live="polite"
    >
      <button
        class="pwa-prompt__close"
        type="button"
        aria-label="بستن"
        @click="dismiss"
      >
        <VIcon icon="tabler-x" size="18" />
      </button>

      <img src="/pwa/icon-white-192-v2.png" alt="" width="46" height="46">

      <div class="pwa-prompt__content">
        <strong>{{ showUpdate ? "نسخه جدید آماده است" : "نصب اپ الومنشی" }}</strong>
        <span v-if="showUpdate">برای دریافت آخرین تغییرات، برنامه را به‌روزرسانی کنید.</span>
        <span v-else-if="isIos">در Safari گزینه Share و سپس «Add to Home Screen» را بزنید.</span>
        <span v-else>برای دسترسی سریع‌تر، الومنشی را روی دستگاه خود نصب کنید.</span>
      </div>

      <button
        v-if="showUpdate"
        class="pwa-prompt__action"
        type="button"
        @click="applyUpdate"
      >
        به‌روزرسانی
      </button>
      <button
        v-else-if="!isIos"
        class="pwa-prompt__action"
        type="button"
        @click="install"
      >
        نصب
      </button>
    </aside>
  </Transition>
</template>

<style scoped>
.pwa-prompt {
  position: fixed;
  z-index: 14000;
  inset-inline: 16px;
  bottom: 84px;
  display: grid;
  max-width: 520px;
  align-items: center;
  padding: 12px 14px;
  margin-inline: auto;
  border: 1px solid rgba(108, 93, 211, 0.16);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 18px 48px rgba(42, 34, 78, 0.18);
  gap: 11px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  backdrop-filter: blur(14px);
}

.pwa-prompt img {
  padding: 4px;
  border-radius: 13px;
  background: #f3f1fb;
  object-fit: contain;
}

.pwa-prompt__content {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.pwa-prompt__content strong {
  color: #302a43;
  font-size: 13px;
}

.pwa-prompt__content span {
  color: #77738a;
  font-size: 11px;
  line-height: 1.8;
}

.pwa-prompt__action,
.pwa-prompt__close {
  border: 0;
  cursor: pointer;
  font: inherit;
}

.pwa-prompt__action {
  min-height: 38px;
  padding: 0 14px;
  border-radius: 11px;
  color: #fff;
  background: #6c5dd3;
  font-size: 11px;
  font-weight: 800;
}

.pwa-prompt__close {
  position: absolute;
  top: -8px;
  inset-inline-start: -8px;
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border: 1px solid #ebe8f2;
  border-radius: 50%;
  color: #77738a;
  background: #fff;
}

.pwa-prompt-enter-active,
.pwa-prompt-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.pwa-prompt-enter-from,
.pwa-prompt-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (min-width: 960px) {
  .pwa-prompt {
    bottom: 24px;
  }
}

@media (max-width: 480px) {
  .pwa-prompt {
    inset-inline: 10px;
    padding: 11px;
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .pwa-prompt__content span {
    font-size: 10px;
  }
}
</style>
